import { useTranslation } from 'react-i18next'

import { ActionFlag, BuilderMenuItem, VariableSelectFeatureFlag } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'

import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useVariableTextSingleCell } from '@/hooks/__project__/useVariableTextSingleCell'
import { SortableItem } from '@/components/SortableItem'

/**
 * SortableMenuItem is a function component that provides an interface for a single sortable menu item in a builder.
 * It also uses the useTranslation hook to get the t function, which is used for translating text.
 *
 * @param {object} props - The props that define the component's behavior and state.
 * @param {number} props.menuIdx - The index of the menu that contains the item.
 * @param {BuilderMenuItem} props.menuItem - The menu item object that contains the details of the menu item.
 *
 * @returns {JSX.Element} Returns a JSX element that renders the sortable menu item interface.
 */
function SortableMenuItem({ menuIdx, menuItem }: { menuIdx: number; menuItem: BuilderMenuItem }) {
  const { t } = useTranslation()

  const { setMenuItemAction, deleteMenuItem } = useAppBuilderContext()

  // The sortable menu item interface

  const menuItemTitle = useVariableTextSingleCell(menuItem.title)

  return (
    <SortableItem
      id={menuItem.id}
      title={menuItemTitle}
      icon={
        menuItem.icon?.flag === VariableSelectFeatureFlag.Icon ? (
          // If the menu item has an icon, display it
          <MaterialIcon size={20} icon={menuItem.icon?.value} />
        ) : menuItem.icon?.flag === 128 ? (
          <img src={menuItem.icon?.value} className='h-6 w-6 aspect-square rounded-full' />
        ) : (
          // If the menu item does not have an icon, display a default icon
          <MaterialIcon size={20} icon='menu' />
        )
      }
      reorderTitle={t('Reorder Menu Item')}
      onEdit={() =>
        // Set the menu item action state to edit when the edit option is selected
        setMenuItemAction({
          flag: ActionFlag.Edit,
          menuItemId: menuItem.id,
        })
      }
      editTitle={t('Edit Menu Item')}
      onDelete={async () =>
        // Delete the menu item when the delete option is selected
        await deleteMenuItem(menuIdx, menuItem.id)
      }
      deleteTitle={t('Delete Menu Item')}
    />
  )
}

export default SortableMenuItem
